import React, { useState, useEffect } from 'react';
import { FiAlertCircle, FiAlertTriangle, FiInfo, FiCheckCircle, FiTarget, FiClock, FiStar } from 'react-icons/fi';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import Button from '../../../components/common/Button';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  Badge,
  AlertCard,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const ActionableInsights = ({ studentId }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getActionableInsights(studentId);
      setData(response.data.actionable_insights);
    } catch (error) {
      showToast('error', 'Failed to load actionable insights');
      console.error('Error fetching actionable insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <FiAlertCircle size={24} />;
      case 'medium':
        return <FiAlertTriangle size={24} />;
      case 'low':
        return <FiInfo size={24} />;
      default:
        return <FiCheckCircle size={24} />;
    }
  };

  const getSeverityEmoji = (severity) => {
    switch (severity) {
      case 'high':
        return '🚨';
      case 'medium':
        return '⚠️';
      case 'low':
        return 'ℹ️';
      default:
        return '✅';
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'optimal_time':
        return '🕐';
      case 'subject_focus':
        return '📚';
      case 'practice':
        return '✍️';
      case 'rest':
        return '💤';
      default:
        return '💡';
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading insights..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiAlertCircle size={64} />
        <h3>No Insights Available</h3>
        <p>Actionable insights will appear once there's sufficient learning data</p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* Summary Overview */}
      <Section>
        <SectionTitle>
          <FiTarget />
          Insights Summary
        </SectionTitle>
        <Grid minWidth="200px">
          <StatCard>
            <StatLabel>Total Alerts</StatLabel>
            <StatValue color={theme.colors.error}>
              {data.summary.total_alerts}
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>High Priority</StatLabel>
            <StatValue color={theme.colors.error}>
              {data.summary.high_priority}
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Subjects Needing Attention</StatLabel>
            <StatValue color={theme.colors.warning}>
              {data.summary.subjects_needing_attention}
            </StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Recommendations</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.summary.recommendations_count}
            </StatValue>
          </StatCard>
        </Grid>
      </Section>

      {/* Critical Alerts */}
      {data.alerts && data.alerts.length > 0 && (
        <Section>
          <SectionTitle>
            <FiAlertCircle />
            Active Alerts
          </SectionTitle>
          <Grid minWidth="350px">
            {data.alerts.map((alert, index) => (
              <AlertCard key={index} severity={alert.severity}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ fontSize: '32px', flexShrink: 0 }}>
                    {getSeverityEmoji(alert.severity)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: theme.colors.textPrimary }}>
                        {alert.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </h3>
                      <Badge
                        color={
                          alert.severity === 'high' ? theme.colors.error :
                          alert.severity === 'medium' ? theme.colors.warning :
                          theme.colors.info
                        }
                      >
                        {alert.severity}
                      </Badge>
                    </div>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.colors.textSecondary }}>
                      {alert.message}
                    </p>
                    {alert.subject && (
                      <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
                        📚 Subject: <strong>{alert.subject}</strong>
                      </div>
                    )}
                    {alert.days_inactive && (
                      <div style={{ fontSize: '13px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                        ⏰ Inactive for: <strong>{alert.days_inactive} days</strong>
                      </div>
                    )}
                  </div>
                </div>
              </AlertCard>
            ))}
          </Grid>
        </Section>
      )}

      {/* At-Risk Subjects */}
      {data.at_risk_subjects && data.at_risk_subjects.length > 0 && (
        <Section>
          <SectionTitle>
            <FiAlertTriangle />
            Subjects Requiring Attention
          </SectionTitle>
          <Grid minWidth="300px">
            {data.at_risk_subjects.map((subject, index) => (
              <Card
                key={index}
                style={{
                  borderLeft: `4px solid ${theme.colors.error}`,
                  background: `${theme.colors.error}10`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', color: theme.colors.textPrimary }}>
                    {subject.subject_name}
                  </h3>
                  <Badge color={theme.colors.error}>
                    {subject.severity}
                  </Badge>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.error }}>
                    {subject.average_score.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                    Average score from {subject.attempts} attempts
                  </div>
                </div>
                
                <div style={{
                  padding: '12px',
                  background: theme.colors.surface,
                  borderRadius: '8px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                }}>
                  💡 This subject needs immediate attention. Consider extra practice or tutoring.
                </div>
                
                <Button size="small" style={{ width: '100%' }}>
                  Review Learning Materials
                </Button>
              </Card>
            ))}
          </Grid>
        </Section>
      )}

      {/* Focus Areas */}
      {data.focus_areas && data.focus_areas.length > 0 && (
        <Section>
          <SectionTitle>
            <FiTarget />
            Specific Focus Areas
          </SectionTitle>
          <Grid minWidth="300px">
            {data.focus_areas.map((area, index) => (
              <Card key={index} style={{ borderLeft: `4px solid ${theme.colors.warning}` }}>
                <div style={{ marginBottom: '12px' }}>
                  <Badge color={theme.colors.warning}>
                    {area.fail_count} failed attempts
                  </Badge>
                </div>
                
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: theme.colors.textPrimary }}>
                  {area.quiz_title}
                </h3>
                
                <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '12px' }}>
                  📚 Subject: <strong>{area.subject}</strong>
                </div>
                
                <div style={{
                  padding: '12px',
                  background: theme.colors.backgroundSecondary,
                  borderRadius: '8px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: theme.colors.textPrimary,
                }}>
                  <strong>Recommendation:</strong><br />
                  {area.recommendation}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button size="small" variant="outline" style={{ flex: 1 }}>
                    Review
                  </Button>
                  <Button size="small" style={{ flex: 1 }}>
                    Practice
                  </Button>
                </div>
              </Card>
            ))}
          </Grid>
        </Section>
      )}

      {/* Optimal Study Times */}
      {data.optimal_study_times && data.optimal_study_times.length > 0 && (
        <Section>
          <SectionTitle>
            <FiClock />
            Optimal Study Times
          </SectionTitle>
          <Card>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
              Based on performance data, these are the best times for your child to study:
            </p>
            <Grid minWidth="200px">
              {data.optimal_study_times.slice(0, 3).map((time, index) => (
                <div
                  key={index}
                  style={{
                    padding: '24px',
                    background: index === 0 
                      ? `linear-gradient(135deg, ${theme.colors.success}20, ${theme.colors.primary}20)`
                      : theme.colors.backgroundSecondary,
                    borderRadius: '12px',
                    border: index === 0 ? `2px solid ${theme.colors.success}` : 'none',
                    textAlign: 'center',
                  }}
                >
                  {index === 0 && (
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                      ⭐
                    </div>
                  )}
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.primary, marginBottom: '8px' }}>
                    {time.time_label}
                  </div>
                  <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                    Average Score
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.success }}>
                    {time.average_score.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '8px' }}>
                    {time.attempts} attempts
                  </div>
                </div>
              ))}
            </Grid>
          </Card>
        </Section>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <Section>
          <SectionTitle>
            <FiStar />
            Personalized Recommendations
          </SectionTitle>
          <Grid minWidth="350px">
            {data.recommendations.map((rec, index) => (
              <Card
                key={index}
                style={{
                  background: `${theme.colors.info}10`,
                  border: `1px solid ${theme.colors.info}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ fontSize: '32px', flexShrink: 0 }}>
                    {getRecommendationIcon(rec.type)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Badge color={theme.colors.info}>
                        {rec.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Badge>
                    </div>
                    <p style={{ margin: 0, fontSize: '15px', color: theme.colors.textPrimary, lineHeight: '1.6' }}>
                      {rec.message}
                    </p>
                    {rec.optimal_hours && rec.optimal_hours.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        background: theme.colors.surface,
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: theme.colors.textSecondary,
                      }}>
                        <strong>Best hours:</strong> {rec.optimal_hours.map(h => `${h}:00`).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </Grid>
        </Section>
      )}

      {/* Positive Insights */}
      {data.positive_insights && data.positive_insights.length > 0 && (
        <Section>
          <SectionTitle>
            ✨ Achievements & Positive Trends
          </SectionTitle>
          <Grid minWidth="350px">
            {data.positive_insights.map((insight, index) => (
              <Card
                key={index}
                style={{
                  background: `linear-gradient(135deg, ${theme.colors.success}15, ${theme.colors.primary}15)`,
                  border: `2px solid ${theme.colors.success}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ fontSize: '32px', flexShrink: 0 }}>
                    🎉
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Badge color={theme.colors.success}>
                        {insight.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Badge>
                    </div>
                    <p style={{ margin: 0, fontSize: '15px', color: theme.colors.textPrimary, fontWeight: 500, lineHeight: '1.6' }}>
                      {insight.message}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </Grid>
        </Section>
      )}

      {/* Action Summary */}
      <Section>
        <Card
          style={{
            background: theme.colors.backgroundSecondary,
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>💪</div>
          <h2 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary }}>
            Take Action Today
          </h2>
          <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: theme.colors.textSecondary, lineHeight: '1.6' }}>
            {data.summary.total_alerts > 0 
              ? `There are ${data.summary.total_alerts} areas requiring attention. Review the alerts above and take action to help your child succeed.`
              : 'Great news! No critical alerts at the moment. Keep encouraging consistent study habits to maintain this positive trajectory.'}
          </p>
          {data.summary.total_alerts > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Button>
                Review Learning Materials
              </Button>
              <Button variant="outline">
                Schedule Study Time
              </Button>
            </div>
          )}
        </Card>
      </Section>
    </>
  );
};

export default ActionableInsights;

